import React, { useMemo } from "react";

import { Box, Heading, HStack } from "@chakra-ui/react";

import { Outlet, useOutletContext } from "react-router-dom";

import { TabbedLinks } from "@/components/layout/TabbedLinks";
import useNestedSubRoute from "@/hooks/navigation/useNestedSubRoute";
import { useCompany } from "@/hooks/util/useCompany";
import { useRoles } from "@/hooks/util/useRoles";

import { NavTabLink } from "./NavTabLink";

export interface Tab {
  to: string;
  text: string;
}

export interface TabbedRouteProps {
  tabs: Array<Tab>;
}

interface TabOutletContext {
  showTabs: boolean;
}

export const TabbedRoute: React.FC<TabbedRouteProps> = ({ tabs }) => {
  const { isNestedSubRoute } = useNestedSubRoute();
  const { selectedCompany, selectedClient } = useCompany();
  const { userCannot } = useRoles();

  const context = useMemo<TabOutletContext>(
    () => ({ showTabs: !isNestedSubRoute }),
    [isNestedSubRoute]
  );

  const tabsToShow = useMemo(() => {
    let links = tabs;

    if (userCannot("company.read")) {
      links = links.filter((item) => item.to !== "practise-profile");
    }

    if (userCannot("company.members.read")) {
      links = links.filter((item) => item.to !== "team");
    }

    if (userCannot("company.reports") && userCannot("company.summary")) {
      links = links.filter((item) => item.to !== "billing");
    }

    if (userCannot("company.csv-statements")) {
      links = links.filter((item) => item.to !== "documents");
    }

    return links;
  }, [tabs, userCannot]);

  return (
    <Box px={12} py={10}>
      <Heading color="secondary.600" fontSize={36} fontWeight={600}>
        Manage {selectedClient ? selectedClient?.name : selectedCompany?.name}
      </Heading>

      {!isNestedSubRoute && (
        <TabbedLinks as={HStack} mb={4} fontWeight={500} mt={7} spacing={5}>
          {tabsToShow.map(({ to, text }) => (
            <NavTabLink
              h={8}
              textDecor="none !important"
              key={to}
              to={to}
              activeOnlyIfExact={to === ""}
            >
              {text}
            </NavTabLink>
          ))}
        </TabbedLinks>
      )}

      <Box>
        <Outlet context={context} />
      </Box>
    </Box>
  );
};

export const useTabContext = () => useOutletContext<TabOutletContext>() ?? {};
